import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ProgressRing } from '../components/ui/ProgressRing';
import { StatCard } from '../components/ui/StatCard';
import { StreakBadge } from '../components/ui/StreakBadge';
import { MilestoneTimeline } from '../components/ui/MilestoneTimeline';
import { AchievementBadges } from '../components/ui/AchievementBadges';
import {
    BookOpen,
    TrendingUp,
    Award,
    Clock,
    ChevronRight,
    Play,
    Target,
    Zap,
    Calendar,
    ArrowUpRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardPage = () => {
    const { user } = useAuth();
    const { t, language, getLocalizedField } = useLanguage();
    const [stats, setStats] = useState(null);
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
        // Record daily check-in for streak
        recordCheckin();
    }, []);

    const recordCheckin = async () => {
        try {
            await axios.post(`${API}/streak/checkin`);
        } catch (error) {
            console.error('Error recording checkin:', error);
        }
    };

    const fetchDashboardData = async () => {
        try {
            const [statsRes, badgesRes] = await Promise.all([
                axios.get(`${API}/progress/stats`),
                axios.get(`${API}/badges`)
            ]);
            setStats(statsRes.data);
            setBadges(badgesRes.data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getLevelConfig = (level) => {
        const configs = {
            basic: { color: 'emerald', borderClass: 'level-basic', icon: BookOpen, gradient: 'from-emerald-500 to-emerald-600' },
            intermediate: { color: 'blue', borderClass: 'level-intermediate', icon: TrendingUp, gradient: 'from-blue-500 to-blue-600' },
            advanced: { color: 'purple', borderClass: 'level-advanced', icon: Target, gradient: 'from-purple-500 to-purple-600' },
            expert: { color: 'amber', borderClass: 'level-expert', icon: Award, gradient: 'from-amber-500 to-amber-600' }
        };
        return configs[level?.toLowerCase()] || configs.basic;
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-40 rounded-2xl" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-36 rounded-2xl" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    const totalLessons = stats?.levels?.reduce((acc, l) => acc + l.total_lessons, 0) || 0;
    const completedLessons = stats?.levels?.reduce((acc, l) => acc + l.completed_lessons, 0) || 0;

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Command Center Header */}
                <div className="command-header" data-testid="command-header">
                    <div className="command-header-grid" />
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <span className="futuristic-badge !bg-white/10 !border-white/20 !text-white">
                                    <Zap className="w-3.5 h-3.5" />
                                    Trading Command Center
                                </span>
                                <StreakBadge />
                            </div>
                            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white">
                                {t('welcomeBack')}, {user?.name?.split(' ')[0]}
                            </h1>
                            <p className="text-slate-400 mt-2 text-lg">
                                {language === 'id' 
                                    ? 'Lanjutkan perjalanan trading Anda'
                                    : 'Continue your trading journey'
                                }
                            </p>
                        </div>
                        <div className="flex items-center gap-6">
                            <ProgressRing 
                                progress={stats?.total_progress || 0}
                                size={130}
                                strokeWidth={10}
                                className="hidden md:flex"
                            />
                        </div>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard
                        title={t('totalProgress')}
                        value={`${stats?.total_progress || 0}%`}
                        subtitle={language === 'id' ? 'Dari semua materi' : 'Of all materials'}
                        icon={TrendingUp}
                        iconColor="bg-primary/10 text-primary"
                        data-testid="stat-total-progress"
                    >
                        <div className="mt-4 progress-futuristic">
                            <div 
                                className="progress-futuristic-bar"
                                style={{ width: `${stats?.total_progress || 0}%` }}
                            />
                        </div>
                    </StatCard>

                    <StatCard
                        title={t('lessonsCompleted')}
                        value={completedLessons}
                        subtitle={`${language === 'id' ? 'dari' : 'of'} ${totalLessons} ${t('lessons').toLowerCase()}`}
                        icon={BookOpen}
                        iconColor="bg-emerald-50 text-emerald-600"
                        data-testid="stat-lessons"
                    />

                    <StatCard
                        title={t('badgesEarned')}
                        value={badges.length}
                        subtitle={`${language === 'id' ? 'dari 4 tersedia' : 'of 4 available'}`}
                        icon={Award}
                        iconColor="bg-amber-50 text-amber-600"
                        data-testid="stat-badges"
                    />
                </div>

                {/* Continue Learning */}
                {stats?.last_lesson && (
                    <div className="glass-card rounded-2xl overflow-hidden" data-testid="continue-learning">
                        <div className="flex flex-col md:flex-row">
                            <div className="md:w-2/5 bg-gradient-to-br from-slate-900 to-slate-800 p-8 flex items-center justify-center min-h-[220px] relative overflow-hidden">
                                <div className="absolute inset-0 opacity-20">
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary rounded-full blur-3xl" />
                                </div>
                                <div className="relative text-center">
                                    <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 hover-lift">
                                        <Play className="w-10 h-10 text-white" />
                                    </div>
                                    <p className="text-white/60 text-sm font-medium">{t('lastWatched')}</p>
                                </div>
                            </div>
                            <div className="md:w-3/5 p-8">
                                <span className="text-xs uppercase tracking-wider text-primary font-semibold">
                                    {t('continueLesson')}
                                </span>
                                <h3 className="text-2xl font-heading font-bold text-slate-900 mt-2">
                                    {getLocalizedField(stats.last_lesson, 'title')}
                                </h3>
                                <p className="text-slate-500 mt-3 line-clamp-2">
                                    {getLocalizedField(stats.last_lesson, 'description') || 
                                        (language === 'id' ? 'Lanjutkan dari posisi terakhir Anda' : 'Continue from where you left off')
                                    }
                                </p>
                                <div className="flex items-center gap-4 mt-4 text-sm text-slate-400">
                                    <span className="flex items-center gap-1.5">
                                        <Clock className="w-4 h-4" />
                                        {stats.last_lesson.duration_minutes} {t('minutes')}
                                    </span>
                                </div>
                                <Link to={`/lesson/${stats.last_lesson.id}`}>
                                    <Button className="mt-6 h-12 px-6 rounded-xl hover-lift hover-glow" data-testid="continue-btn">
                                        {t('continueWatching')}
                                        <ArrowUpRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                {/* Learning Path Progress */}
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-heading font-bold text-slate-900">
                                {t('learningPath')}
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                                {language === 'id' ? '4 level pembelajaran terstruktur' : '4 structured learning levels'}
                            </p>
                        </div>
                        <Link to="/learning">
                            <Button variant="ghost" className="text-slate-600 hover:text-primary" data-testid="view-all-learning">
                                {t('viewAll')}
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {stats?.levels?.map((level) => {
                            const config = getLevelConfig(level.name);
                            const Icon = config.icon;

                            return (
                                <Link key={level.id} to={`/learning/${level.name.toLowerCase()}`}>
                                    <div 
                                        className={`level-card ${config.borderClass} p-6 h-full`}
                                        data-testid={`level-card-${level.name.toLowerCase()}`}
                                    >
                                        <div className="flex items-center gap-3 mb-5">
                                            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}>
                                                <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                                            </div>
                                            <div>
                                                <h3 className="font-heading font-semibold text-slate-900">
                                                    {language === 'en' && level.name_en ? level.name_en : level.name}
                                                </h3>
                                                <p className="text-xs text-slate-400">
                                                    {level.completed_lessons}/{level.total_lessons} {t('lessons').toLowerCase()}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="progress-futuristic">
                                            <div 
                                                className="progress-futuristic-bar"
                                                style={{ 
                                                    width: `${level.progress}%`,
                                                    background: `linear-gradient(90deg, var(--tw-gradient-stops))`,
                                                    '--tw-gradient-from': config.color === 'emerald' ? '#10B981' : 
                                                                          config.color === 'blue' ? '#3B82F6' :
                                                                          config.color === 'purple' ? '#8B5CF6' : '#F59E0B',
                                                    '--tw-gradient-to': config.color === 'emerald' ? '#059669' : 
                                                                        config.color === 'blue' ? '#2563EB' :
                                                                        config.color === 'purple' ? '#7C3AED' : '#D97706'
                                                }}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between mt-3">
                                            <span className="text-sm font-medium text-slate-600">{level.progress}%</span>
                                            <ChevronRight className="w-4 h-4 text-slate-400" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* Milestone Timeline */}
                <MilestoneTimeline maxItems={6} />

                {/* Achievement Badges Section */}
                <div className="glass-card rounded-2xl p-6">
                    <AchievementBadges showAll={true} />
                </div>
            </div>
        </DashboardLayout>
    );
};
